import { useState, useMemo, useEffect } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import TabButton from '../components/TabButton'
import CurrencyCard from '../components/CurrencyCard'
import ConfirmModal from '../components/ConfirmModal'
import LoadingModal from '../components/LoadingModal'
import TradeTypeCard from '../components/TradeTypeCard'
import TransferIcon from '../components/icons/TransferIcon'
import WalletIcon from '../components/icons/WalletIcon'
import HistoryIcon from '../components/icons/HistoryIcon'
import CloseIcon from '../components/icons/CloseIcon'
import SendIcon from '../components/icons/SendIcon'
import HistoryTab from '../components/HistoryTab'
import CloseTab from '../components/CloseTab'
import TransferTab from '../components/TransferTab'
import FlagIcon from '../components/FlagIcon'
import Keypad from '../components/Keypad'
import PassportScanModal from '../components/PassportScanModal'
import TransactionResultModal from '../components/TransactionResultModal'
import RateModificationModal from '../components/RateModificationModal'
import ForeignAmountModificationModal from '../components/ForeignAmountModificationModal'
import { printReceipt } from '../utils/printer'
import type { ReceiptTransactionData } from '../types/receipt'
import { useExchange } from '../hooks/useExchange'
import { useBillCounter } from '../hooks/useBillCounter'
import { createExchange, startUpload, uploadToS3, dataUrlToBlob } from '../api/services'
import { ApiError } from '../api/client'
import type { ExchangeCreateRequest, ExchangeTransaction, Passport } from '../api/types'
import { lastTransactionAtom, environmentAtom } from '../state/atoms'
import { useBroadcastChannel } from '../hooks/useBroadcastChannel'

import ExchangeTab from '../components/ExchangeTab'
import { currencies, CURRENCY_MIN_UNITS } from '../constants/currencies'
import { getCartLineKey, mergeCartLine, type CartLine } from '../utils/transactionCart'

// 더블 탭 감지 딜레이 (ms)
const DOUBLE_TAP_DELAY = 300

function Home() {
  const [mainTab, setMainTab] = useState<'transaction' | 'cash' | 'history' | 'close' | 'transfer'>('transaction')
  const [tradeType, setTradeType] = useState<'buy' | 'sell'>('buy')
  const [exchangeMode, setExchangeMode] = useState(false) // 통화간 교환 모드 (#7)
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD')
  const [foreignAmount, setForeignAmount] = useState<string>('')
  const [inputKRW, setInputKRW] = useState<string>('') // 매각 시 원화 입력
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [transactionError, setTransactionError] = useState<string | null>(null)
  const [isCreatingTransaction, setIsCreatingTransaction] = useState(false)
  const [showReprintButton, setShowReprintButton] = useState(false)
  const [lastTransaction, setLastTransaction] = useRecoilState(lastTransactionAtom)
  const environment = useRecoilValue(environmentAtom)
  const [showPassportScanModal, setShowPassportScanModal] = useState(false)
  const [scannedPassportImage, setScannedPassportImage] = useState<string | null>(null)
  const [passportSkipped, setPassportSkipped] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('거래 처리 중...')
  const [showResultModal, setShowResultModal] = useState(false)
  const [resultModalError, setResultModalError] = useState<string | null>(null)
  const [showReprintErrorModal, setShowReprintErrorModal] = useState(false)
  const [reprintErrorMessage, setReprintErrorMessage] = useState<string | null>(null)
  const [isCounterKRW, setIsCounterKRW] = useState(false)
  const [counterWarning, setCounterWarning] = useState<string | null>(null)
  const [countedKRW, setCountedKRW] = useState<number>(0)
  const [sellInputMode, setSellInputMode] = useState<'krw' | 'foreign'>('krw')
  const [foreignSellInput, setForeignSellInput] = useState<string>('')
  const [useTenUnit, setUseTenUnit] = useState(false)

  // 복수 거래 항목 카트 — 다른 통화뿐 아니라 같은 통화의 다른 환율도 분리 (#5, #9)
  const [cart, setCart] = useState<CartLine[]>([])

  // 환율 변경 관련 상태
  const [rateOverride, setRateOverride] = useState<{ rate: number; reason: string } | null>(null)
  const [showRateModal, setShowRateModal] = useState(false)
  const [lastTapTime, setLastTapTime] = useState<number>(0)
  const [foreignAmountOverride, setForeignAmountOverride] = useState<number | null>(null)
  const [showForeignAmountModal, setShowForeignAmountModal] = useState(false)
  const [lastForeignAmountTapTime, setLastForeignAmountTapTime] = useState<number>(0)

  const selectedCurrencyInfo = useMemo(
    () => currencies.find(c => c.code === selectedCurrency) ?? currencies[0],
    [selectedCurrency]
  )

  // 외화 금액 포맷팅 (3자리 쉼표)
  const formatForeignAmount = (value: string): string => {
    if (!value) return ''
    const num = parseInt(value, 10)
    if (Number.isNaN(num)) return ''
    return num.toLocaleString('ko-KR')
  }

  // 입력값에서 숫자만 추출
  const extractNumbers = (value: string): string => {
    return value.replace(/[^0-9]/g, '')
  }

  // Use Exchange Context
  const {
    selectedBranch,
    branches,
    setSelectedBranch,
    getRate,
    isLoadingRates,
    ratesError,
    refreshRates,
  } = useExchange()

  // BroadcastChannel for Customer Display
  const { sendTransactionUpdate, sendDisplayModeChange, sendCounterData } = useBroadcastChannel({
    windowId: 'staff-pos',
  })
  const { lastCounterData, clearLastData } = useBillCounter()

  // Get current exchange rate from API
  const currentRate = useMemo(() => {
    return getRate(selectedCurrency, tradeType)
  }, [selectedCurrency, tradeType, getRate])

  // 적용 환율 (override 또는 기본 환율)
  const effectiveRate = useMemo(() => {
    return rateOverride?.rate ?? currentRate
  }, [rateOverride, currentRate])

  // Calculate KRW amount (round to nearest 100 KRW) - 매입용
  const calculatedKRW = useMemo(() => {
    if (!foreignAmount || !effectiveRate) return 0
    const rawKRW = parseFloat(foreignAmount) * effectiveRate
    if (Number.isNaN(rawKRW)) return 0
    return Math.round(rawKRW / 100) * 100
  }, [foreignAmount, effectiveRate])

  // 매각 시 원화 기준 계산 (원화 입력 모드)
  const sellCalculation = useMemo(() => {
    if (tradeType !== 'sell' || sellInputMode !== 'krw' || !inputKRW || !effectiveRate) {
      return { foreignAmount: 0, actualKRW: 0, change: 0, calculatedForeignAmount: 0, isOverridden: false }
    }
    const receivedKRW = parseFloat(inputKRW)
    if (Number.isNaN(receivedKRW) || receivedKRW <= 0) {
      return { foreignAmount: 0, actualKRW: 0, change: 0, calculatedForeignAmount: 0, isOverridden: false }
    }

    const defaultMinUnit = CURRENCY_MIN_UNITS[selectedCurrency] || 1
    const minUnit = useTenUnit ? 10 : defaultMinUnit
    const calculatedForeignAmt = Math.floor((receivedKRW / effectiveRate) / minUnit) * minUnit

    const foreignAmt = foreignAmountOverride !== null ? foreignAmountOverride : calculatedForeignAmt
    const isOverridden = foreignAmountOverride !== null

    const actualKRW = Math.round((foreignAmt * effectiveRate) / 100) * 100
    const change = Math.round((receivedKRW - actualKRW) / 100) * 100
    return { foreignAmount: foreignAmt, actualKRW, change, calculatedForeignAmount: calculatedForeignAmt, isOverridden }
  }, [tradeType, sellInputMode, inputKRW, effectiveRate, selectedCurrency, useTenUnit, foreignAmountOverride])

  // 매각 시 외화 기준 계산 (외화 입력 모드)
  const sellForeignCalculation = useMemo(() => {
    if (tradeType !== 'sell' || sellInputMode !== 'foreign' || !foreignSellInput || !effectiveRate) {
      return { foreignAmount: 0, requiredKRW: 0 }
    }
    const foreignAmt = parseFloat(foreignSellInput)
    if (Number.isNaN(foreignAmt) || foreignAmt <= 0) {
      return { foreignAmount: 0, requiredKRW: 0 }
    }

    const requiredKRW = Math.round((foreignAmt * effectiveRate) / 100) * 100
    return { foreignAmount: foreignAmt, requiredKRW }
  }, [tradeType, sellInputMode, foreignSellInput, effectiveRate])

  const sellFinalValues = useMemo(() => {
    if (sellInputMode === 'krw') {
      return {
        foreignAmount: sellCalculation.foreignAmount,
        actualKRW: sellCalculation.actualKRW,
        change: sellCalculation.change,
        receivedKRW: parseFloat(inputKRW) || 0,
        calculatedForeignAmount: sellCalculation.calculatedForeignAmount,
        isOverridden: sellCalculation.isOverridden,
      }
    }
    return {
      foreignAmount: sellForeignCalculation.foreignAmount,
      actualKRW: sellForeignCalculation.requiredKRW,
      change: 0,
      receivedKRW: sellForeignCalculation.requiredKRW,
      calculatedForeignAmount: sellForeignCalculation.foreignAmount,
      isOverridden: false,
    }
  }, [sellInputMode, sellCalculation, sellForeignCalculation, inputKRW])

  // 현재 입력 중인 통화를 카트 라인으로 환산 (#5)
  const currentLine = useMemo(() => {
    if (tradeType === 'buy') {
      const amt = parseFloat(foreignAmount) || 0
      return { valid: amt > 0 && effectiveRate > 0, foreignAmount: amt, krwAmount: calculatedKRW }
    }
    const amt = sellFinalValues.foreignAmount
    return { valid: amt > 0 && effectiveRate > 0, foreignAmount: amt, krwAmount: sellFinalValues.actualKRW }
  }, [tradeType, foreignAmount, calculatedKRW, sellFinalValues, effectiveRate])

  // 카트 + 현재 입력을 합친 전체 라인 (등록/합계/표시용)
  const allLines = useMemo(() => {
    if (!currentLine.valid) return cart
    return mergeCartLine(cart, {
      currency: selectedCurrency,
      foreignAmount: currentLine.foreignAmount,
      krwAmount: currentLine.krwAmount,
      rate: effectiveRate,
      rateOverridden: !!rateOverride,
      rateOverrideReason: rateOverride?.reason,
    })
  }, [cart, currentLine, selectedCurrency, effectiveRate, rateOverride])

  const totalKRW = useMemo(() => allLines.reduce((sum, l) => sum + l.krwAmount, 0), [allLines])
  const hasMultipleLines = allLines.length > 1

  const clearCurrentInputs = () => {
    setForeignAmount('')
    setInputKRW('')
    setForeignSellInput('')
    setForeignAmountOverride(null)
    setRateOverride(null)
    setIsCounterKRW(false)
    setCountedKRW(0)
  }

  // 현재 입력을 카트에 저장
  const commitCurrentInput = () => {
    if (!currentLine.valid) return
    setCart(allLines)
    clearCurrentInputs()
  }

  // 통화 선택: 입력 금액은 유지한 채 통화만 전환 (카트 추가는 '거래 항목 추가하기' 버튼으로만)
  const handleSelectCurrency = (code: string) => {
    if (showReprintButton) return
    setSelectedCurrency(code)
  }

  const handleRemoveCartLine = (index: number) => {
    setCart(prev => prev.filter((_, lineIndex) => lineIndex !== index))
  }

  // 더블 탭 핸들러 (환율 변경)
  const handleRateTap = () => {
    const now = Date.now()
    if (now - lastTapTime < DOUBLE_TAP_DELAY) {
      setShowRateModal(true)
      setLastTapTime(0)
    } else {
      setLastTapTime(now)
    }
  }

  // 환율 변경 확인
  const handleRateConfirm = (newRate: number, reason: string) => {
    setRateOverride({ rate: newRate, reason })
    setShowRateModal(false)
  }

  const handleForeignAmountTap = () => {
    if (tradeType !== 'sell' || sellCalculation.foreignAmount <= 0) return
    const now = Date.now()
    if (now - lastForeignAmountTapTime < DOUBLE_TAP_DELAY) {
      setShowForeignAmountModal(true)
      setLastForeignAmountTapTime(0)
    } else {
      setLastForeignAmountTapTime(now)
    }
  }

  const handleForeignAmountConfirm = (newAmount: number) => {
    setForeignAmountOverride(newAmount)
    setShowForeignAmountModal(false)
  }

  // 통화 변경 시 환율/외화 override 초기화
  useEffect(() => {
    setRateOverride(null)
    setForeignAmountOverride(null)
  }, [selectedCurrency])

  // 거래 타입 변경 시 입력값 및 환율 override 초기화
  useEffect(() => {
    setForeignAmount('')
    if (!isCounterKRW) {
      setInputKRW('')
    }
    setRateOverride(null)
    setForeignAmountOverride(null)
    setSellInputMode('krw')
    setForeignSellInput('')
    setUseTenUnit(false)
  }, [tradeType, isCounterKRW])

  // 매입/매각 혼합 불가 — 거래 타입이 바뀌면 카트/계수값 초기화
  // (계수값은 isCounterKRW 변경에 휩쓸려 지워지면 안 되므로 여기서만 초기화 — #8-3)
  useEffect(() => {
    setCart([])
    setCountedKRW(0)
  }, [tradeType])

  useEffect(() => {
    if (!lastCounterData) return

    sendCounterData({
      currency: lastCounterData.currency,
      totalAmount: lastCounterData.totalAmount,
      denominations: lastCounterData.denominations,
      isFinal: !!lastCounterData.isFinal,
      timestamp: lastCounterData.timestamp,
    })


    if (isCreatingTransaction) {
      clearLastData()
      return
    }

    const { currency, totalAmount } = lastCounterData

    if (mainTab === 'transaction') {
      if (currency === 'KRW') {
        if (tradeType === 'buy') {
          setCountedKRW(totalAmount)
          setCounterWarning(null)
        } else if (!showReprintButton) {
          setSellInputMode('krw')
          setInputKRW(totalAmount.toString())
          setIsCounterKRW(true)
          // 매각도 원화 기준으로 총액 바와 비교 (#8-3)
          setCountedKRW(totalAmount)
          setCounterWarning(null)
        }
      } else {
        const currencyExists = currencies.some((c) => c.code === currency)
        if (currencyExists) {
          setSelectedCurrency(currency)
          if (tradeType === 'buy') {
            if (!showReprintButton) {
              setForeignAmount(totalAmount.toString())
            }
          } else if (!showReprintButton) {
            setSellInputMode('foreign')
            setForeignSellInput(totalAmount.toString())
            setIsCounterKRW(false)
          }
          setCounterWarning(null)
        } else {
          setCounterWarning(`지원하지 않는 통화입니다: ${currency}`)
        }
      }
    }

    clearLastData()
  }, [
    lastCounterData,
    mainTab,
    tradeType,
    showReprintButton,
    isCreatingTransaction,
    clearLastData,
    sendCounterData,
    sendDisplayModeChange,
  ])

  // 받은 원화 변경 시 외화 override 초기화
  useEffect(() => {
    setForeignAmountOverride(null)
  }, [inputKRW])

  // 실시간으로 Customer Display에 거래 정보 전송
  useEffect(() => {
    // 교환 모드에서는 ExchangeTab이 고객 디스플레이 전송을 담당 (#7)
    if (exchangeMode && mainTab === 'transaction') {
      return
    }

    // 거래 탭이 아니면 idle 상태 전송
    if (mainTab !== 'transaction') {
      // 단, 거래 완료(영수증 출력 후) 상태면 직원이 탭을 옮겨도 고객 화면은 거래 결과 유지 (#6-2)
      if (showReprintButton) {
        return
      }
      sendDisplayModeChange({ mode: 'idle' })
      sendTransactionUpdate({
        amount: 0,
        currency: selectedCurrency,
        rate: 0,
        krwAmount: 0,
        tradeType,
        status: 'idle',
      })
      return
    }

    // 복수 거래 항목 사용 중 - 통화/환율별 라인과 총액 전송 (#5, #9)
    if (cart.length > 0) {
      const lines = allLines.map(l => ({
        currency: l.currency,
        amount: l.foreignAmount,
        rate: l.rate,
        krwAmount: l.krwAmount,
      }))
      sendDisplayModeChange({ mode: showReprintButton ? 'complete' : 'transaction' })
      sendTransactionUpdate({
        amount: lines[0]?.amount ?? 0,
        currency: lines[0]?.currency ?? selectedCurrency,
        rate: lines[0]?.rate ?? 0,
        krwAmount: totalKRW,
        tradeType,
        status: showReprintButton ? 'complete' : 'processing',
        lines,
        totalKRW,
      })
      return
    }

    // 새 거래 대기 중 (영수증 재출력 버튼 표시 중) - 거래 결과를 보조 디스플레이에 유지
    if (showReprintButton) {
      sendDisplayModeChange({ mode: 'complete' })
      if (tradeType === 'buy') {
        sendTransactionUpdate({
          amount: parseFloat(foreignAmount) || 0,
          currency: selectedCurrency,
          rate: effectiveRate || 0,
          krwAmount: calculatedKRW || 0,
          tradeType: 'buy',
          status: 'complete',
        })
      } else {
        sendTransactionUpdate({
          amount: sellFinalValues.foreignAmount || 0,
          currency: selectedCurrency,
          rate: effectiveRate || 0,
          krwAmount: sellFinalValues.actualKRW || 0,
          tradeType: 'sell',
          status: 'complete',
        })
      }
      return
    }

    // 입력값이 없으면 환율 미리보기 또는 idle 상태
    const hasInput = tradeType === 'buy'
      ? foreignAmount && parseFloat(foreignAmount) > 0
      : sellInputMode === 'krw'
        ? inputKRW && parseFloat(inputKRW) > 0
        : foreignSellInput && parseFloat(foreignSellInput) > 0

    if (!hasInput) {
      if (effectiveRate) {
        // 통화 선택됨, 금액 미입력 → 보조 디스플레이에 해당 통화 환율 표시
        sendDisplayModeChange({ mode: 'transaction' })
        sendTransactionUpdate({
          amount: 0,
          currency: selectedCurrency,
          rate: effectiveRate,
          krwAmount: 0,
          tradeType,
          status: 'rate-preview',
        })
      } else {
        sendDisplayModeChange({ mode: 'idle' })
        sendTransactionUpdate({
          amount: 0,
          currency: selectedCurrency,
          rate: 0,
          krwAmount: 0,
          tradeType,
          status: 'idle',
        })
      }
      return
    }

    // 거래 정보 전송
    if (tradeType === 'buy') {
      sendDisplayModeChange({ mode: 'transaction' })
      sendTransactionUpdate({
        amount: parseFloat(foreignAmount),
        currency: selectedCurrency,
        rate: effectiveRate,
        krwAmount: calculatedKRW,
        tradeType: 'buy',
        status: 'processing',
      })
    } else {
      sendDisplayModeChange({ mode: 'transaction' })
      sendTransactionUpdate({
        amount: sellFinalValues.foreignAmount,
        currency: selectedCurrency,
        rate: effectiveRate,
        krwAmount: sellFinalValues.actualKRW,
        tradeType: 'sell',
        status: 'processing',
      })
    }
  }, [
    mainTab,
    tradeType,
    foreignAmount,
    inputKRW,
    foreignSellInput,
    sellInputMode,
    selectedCurrency,
    effectiveRate,
    calculatedKRW,
    sellFinalValues,
    showReprintButton,
    cart.length,
    allLines,
    totalKRW,
    exchangeMode,
    sendDisplayModeChange,
    sendTransactionUpdate,
  ])

  const handleReset = () => {
    setSelectedCurrency('USD')
    setCart([])
    setForeignAmount('')
    setInputKRW('')
    setShowConfirmModal(false)
    setTransactionError(null)
    setShowReprintButton(false)
    setTradeType('buy')
    setScannedPassportImage(null)
    setPassportSkipped(false)
    setRateOverride(null)
    setForeignAmountOverride(null)
    setIsCounterKRW(false)
    setCounterWarning(null)
    setCountedKRW(0)
    setSellInputMode('krw')
    setForeignSellInput('')
    setUseTenUnit(false)
    setShowForeignAmountModal(false)
  }

  const handleComplete = async () => {
    if (!selectedBranch) {
      setTransactionError('브랜치가 선택되지 않았습니다.')
      return
    }

    setIsCreatingTransaction(true)
    setTransactionError(null)

    try {
      let passportData: Passport = 'Unknown'

      // 1. Upload passport image if exists
      if (scannedPassportImage) {
        setLoadingMessage('여권사진 업로드중...')

        // Get presigned URL
        const uploadResponse = await startUpload({
          branchNid: selectedBranch.nid,
          fileType: 'passport',
          contentType: 'image/jpeg',
        })

        // Convert data URL to Blob and upload to S3
        const blob = dataUrlToBlob(scannedPassportImage)
        await uploadToS3(uploadResponse.url, blob, 'image/jpeg')

        // Set passport data with imageKey
        passportData = {
          number: '',
          imageKey: uploadResponse.key,
        }

        console.log('여권 이미지 업로드 성공:', uploadResponse.key)
      }

      if (cart.length === 0) {
        // ===== 단일 통화 (기존 경로 그대로 — 받은 원화/거스름돈 유지) =====
        setLoadingMessage(tradeType === 'buy' ? '매입 정보 등록중...' : '매각 정보 등록중...')

        // 환율 변경 memo 생성
        const rateMemo = rateOverride
          ? `환율변경: ${rateOverride.rate}, 사유: ${rateOverride.reason}`
          : undefined

        const exchangeRequest: ExchangeCreateRequest = {
          branchNid: selectedBranch.nid,
          fromCurrency: tradeType === 'buy' ? selectedCurrency : 'KRW',
          fromAmount: tradeType === 'buy' ? foreignAmount : sellFinalValues.actualKRW.toString(),
          toCurrency: tradeType === 'buy' ? 'KRW' : selectedCurrency,
          customer: {
            passport: passportData
          },
          expectedToAmount: tradeType === 'buy' ? calculatedKRW.toString() : sellFinalValues.foreignAmount.toString(),
          memo: rateMemo,
        }

        const exchangeResponse = await createExchange(exchangeRequest)

        console.log('거래 생성 성공:', exchangeResponse.exchange)

        const transactionData: ReceiptTransactionData = {
          branchNid: selectedBranch.nid,
          branchName: selectedBranch.name,
          transactionNumber: exchangeResponse.exchange.nid,
          selectedCurrency,
          rate: effectiveRate.toString(),
          foreignAmount: tradeType === 'buy' ? foreignAmount : sellFinalValues.foreignAmount.toString(),
          krwAmount: tradeType === 'buy' ? calculatedKRW.toString() : sellFinalValues.actualKRW.toString(),
          tradeType,
          timestamp: new Date(),
          // 매각 시 추가 필드
          ...(tradeType === 'sell' && {
            receivedKRW: sellFinalValues.receivedKRW.toString(),
            change: sellFinalValues.change.toString(),
          }),
        }

        setLastTransaction({
          transactionData,
          exchangeResponse: exchangeResponse.exchange
        })

        const printResult = await printReceipt(transactionData)

        if (printResult.success) {
          console.log('영수증 발행 성공')
          setShowReprintButton(true)
        } else {
          console.warn('영수증 발행 실패:', printResult.message)
          setResultModalError(printResult.message || '영수증 발행에 실패했습니다.')
          setShowResultModal(true)
        }

        // Note: DO NOT call handleReset() - keep transaction info visible
      } else {
        // ===== 복수 항목: 통화/환율별 개별 거래 등록 + 합산 영수증 한 장 =====
        const linesToRegister = allLines
        const isBuy = tradeType === 'buy'
        const succeeded: { line: CartLine; nid: string; exchange: ExchangeTransaction }[] = []
        let failedAt = -1
        let failMessage = ''

        for (let i = 0; i < linesToRegister.length; i++) {
          const line = linesToRegister[i]
          setLoadingMessage(`${line.currency} ${isBuy ? '매입' : '매각'} 등록중... (${i + 1}/${linesToRegister.length})`)
          try {
            const exchangeRequest: ExchangeCreateRequest = {
              branchNid: selectedBranch.nid,
              fromCurrency: isBuy ? line.currency : 'KRW',
              fromAmount: isBuy ? line.foreignAmount.toString() : line.krwAmount.toString(),
              toCurrency: isBuy ? 'KRW' : line.currency,
              customer: {
                passport: passportData
              },
              expectedToAmount: isBuy ? line.krwAmount.toString() : line.foreignAmount.toString(),
              memo: line.rateOverridden
                ? `환율변경: ${line.rate}, 사유: ${line.rateOverrideReason || ''}`
                : undefined,
            }
            const exchangeResponse = await createExchange(exchangeRequest)
            succeeded.push({ line, nid: exchangeResponse.exchange.nid, exchange: exchangeResponse.exchange })
          } catch (err) {
            console.error(`${line.currency} 거래 생성 실패:`, err)
            failedAt = i
            failMessage = err instanceof ApiError ? err.message : '거래 생성 중 오류가 발생했습니다.'
            break
          }
        }

        if (succeeded.length === 0) {
          setTransactionError(`거래 생성 실패 (${linesToRegister[0].currency}): ${failMessage}`)
          return
        }

        const registeredTotal = succeeded.reduce((sum, x) => sum + x.line.krwAmount, 0)
        const first = succeeded[0]
        const transactionData: ReceiptTransactionData = {
          branchNid: selectedBranch.nid,
          branchName: selectedBranch.name,
          transactionNumber: first.nid,
          selectedCurrency: first.line.currency,
          rate: first.line.rate.toString(),
          foreignAmount: first.line.foreignAmount.toString(),
          krwAmount: first.line.krwAmount.toString(),
          tradeType,
          timestamp: new Date(),
          ...(succeeded.length > 1 && {
            lines: succeeded.map(x => ({
              currency: x.line.currency,
              rate: x.line.rate.toString(),
              foreignAmount: x.line.foreignAmount.toString(),
              krwAmount: x.line.krwAmount.toString(),
              transactionNumber: x.nid,
            })),
            totalKRW: registeredTotal.toString(),
          }),
        }

        setLastTransaction({
          transactionData,
          exchangeResponse: first.exchange
        })

        if (failedAt >= 0) {
          // 실패한 항목부터는 카트에 남겨 재시도 가능하게 유지
          setCart(linesToRegister.slice(failedAt))
          clearCurrentInputs()
          setTransactionError(
            `${linesToRegister[failedAt].currency} 등록 실패: ${failMessage} — 등록된 ${succeeded.length}건은 영수증이 출력되며, 실패한 항목은 목록에 남아 있습니다.`
          )
        } else {
          // 전체 성공: 카트를 등록된 상태로 확정해 새 거래 전까지 표시 유지
          setCart(linesToRegister)
          clearCurrentInputs()
        }

        const printResult = await printReceipt(transactionData)

        if (printResult.success) {
          console.log('영수증 발행 성공')
          if (failedAt < 0) {
            setShowReprintButton(true)
          }
        } else {
          console.warn('영수증 발행 실패:', printResult.message)
          setResultModalError(printResult.message || '영수증 발행에 실패했습니다.')
          setShowResultModal(true)
        }
      }

    } catch (error) {
      console.error('거래 생성 실패:', error)

      if (error instanceof ApiError) {
        setTransactionError(`거래 생성 실패: ${error.message}`)
      } else {
        setTransactionError('거래 생성 중 오류가 발생했습니다.')
      }
    } finally {
      setIsCreatingTransaction(false)
    }
  }

  const handleReprint = async () => {
    if (!lastTransaction?.transactionData) {
      console.error('재출력할 거래 정보가 없습니다.')
      return
    }

    setLoadingMessage('재출력중...')
    setIsCreatingTransaction(true)

    const printResult = await printReceipt(lastTransaction.transactionData)

    setIsCreatingTransaction(false)

    if (printResult.success) {
      console.log('영수증 재출력 성공')
    } else {
      console.warn('영수증 재출력 실패:', printResult.message)
      setReprintErrorMessage(printResult.message || '프린터 연결을 확인해주세요.')
      setShowReprintErrorModal(true)
    }
  }

  const handleNewTransaction = () => {
    // Clear stored transaction data
    setLastTransaction({
      transactionData: null,
      exchangeResponse: null
    })

    // Reset form
    handleReset()

    // Hide reprint button
    setShowReprintButton(false)
    setIsCounterKRW(false)
    setCounterWarning(null)
    setCountedKRW(0)
  }

  return (
    <div className="flex flex-col h-full p-4 lg:p-6 fhd:p-8 gap-4 lg:gap-6 bg-fx-bg">
      {/* 상단 탭 메뉴 */}
      <div className={`flex border-b ${environment === 'development' ? 'border-red-300 bg-red-100' : 'border-gray-200 bg-fx-tab-bg'}`}>
        <TabButton
          icon={<TransferIcon />}
          label="거래"
          isActive={mainTab === 'transaction'}
          onClick={() => setMainTab('transaction')}
          variant="simple"
        />
        <TabButton
          icon={<WalletIcon />}
          label="시재"
          isActive={mainTab === 'cash'}
          onClick={() => setMainTab('cash')}
          variant="simple"
        />
        <TabButton
          icon={<HistoryIcon />}
          label="기록"
          isActive={mainTab === 'history'}
          onClick={() => setMainTab('history')}
          variant="simple"
        />
        <TabButton
          icon={<CloseIcon />}
          label="마감"
          isActive={mainTab === 'close'}
          onClick={() => setMainTab('close')}
          variant="simple"
        />
        <TabButton
          icon={<SendIcon />}
          label="자금이동"
          isActive={mainTab === 'transfer'}
          onClick={() => setMainTab('transfer')}
          variant="simple"
        />
      </div>

      {/* 거래 페이지 */}
      {mainTab === 'transaction' && (
        <>
          {/* Branch Selector (if multiple branches) */}
          {branches.length > 1 && (
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-fx-text-muted mb-2">
                브랜치 선택
              </label>
              <select
                value={selectedBranch?.nid || ''}
                onChange={(e) => {
                  const branch = branches.find(b => b.nid === e.target.value)
                  if (branch) setSelectedBranch(branch)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-fx-buy"
              >
                {branches.map(branch => (
                  <option key={branch.nid} value={branch.nid}>
                    {branch.name} ({branch.role})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Error Display */}
          {ratesError && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center justify-between">
              <span>{ratesError}</span>
              <button
                onClick={refreshRates}
                className="text-sm text-red-600 hover:text-red-800 underline"
              >
                다시 시도
              </button>
            </div>
          )}

          {transactionError && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
              {transactionError}
            </div>
          )}

          {counterWarning && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 p-4 rounded-lg flex items-center justify-between">
              <span>{counterWarning}</span>
              <button
                onClick={() => setCounterWarning(null)}
                className="text-sm text-yellow-600 hover:text-yellow-800"
              >
                닫기
              </button>
            </div>
          )}

          {/* 매입/매각/교환 카드 */}
          <div className="flex gap-4">
            <TradeTypeCard
              type="buy"
              isActive={!exchangeMode && tradeType === 'buy'}
              onClick={() => {
                setExchangeMode(false)
                setTradeType('buy')
              }}
            />
            <TradeTypeCard
              type="sell"
              isActive={!exchangeMode && tradeType === 'sell'}
              onClick={() => {
                setExchangeMode(false)
                setTradeType('sell')
              }}
            />
            <TradeTypeCard
              type="exchange"
              isActive={exchangeMode}
              onClick={() => setExchangeMode(true)}
            />
            <button
                onClick={refreshRates}
                className="bg-fx-reset text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-all"
                disabled={isLoadingRates}
              >
                {isLoadingRates ? "로딩 중..." : '↻ 환율 새로고침'}
            </button>

          </div>

          {/* 교환 모드 (#7) */}
          {exchangeMode && <ExchangeTab />}

          {/* 통화 선택 및 금액 입력 */}
          {!exchangeMode && (
          <div className="flex gap-6 flex-1 min-h-0">
              {/* 왼쪽: 통화 그리드 */}
              <div className={`flex-1 min-w-0 bg-fx-currency-bg p-6 rounded-lg overflow-y-auto transition-opacity duration-300 ${showReprintButton ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="grid grid-cols-4 gap-3">
                  {currencies.map((currency) => (
                    <CurrencyCard
                      key={currency.code}
                      code={currency.code}
                      flagCode={currency.countryCode}
                      isSelected={selectedCurrency === currency.code}
                      inCart={cart.some(l => l.currency === currency.code)}
                      onClick={() => handleSelectCurrency(currency.code)}
                    />
                  ))}
                </div>
              </div>

              {/* 중앙: 선택한 통화 정보 + 거래 항목별 합계 */}
              <div className="flex-1 min-w-0 min-h-0 flex flex-col gap-4">
              {tradeType === 'sell' && (
                <div className="w-full bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg text-center font-bold">
                  매각중
                </div>
              )}
          
                <div className="bg-white p-6 rounded-lg border border-gray-200 flex flex-col gap-4 flex-none">
                  {/* 헤더: 통화 + 적용 환율 (목업 레이아웃 — 한 행에 배치) */}
                  <div className="flex items-center gap-3">
                    <FlagIcon
                      countryCode={selectedCurrencyInfo.countryCode}
                      size="xl"
                      className="text-5xl"
                      title={selectedCurrencyInfo.name}
                    />
                    <span className="text-3xl font-bold text-fx-text-dark">
                      {selectedCurrency}
                    </span>
                    <div className="flex-1" />
                    <div
                      onClick={handleRateTap}
                      className="cursor-pointer text-right px-3 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="text-[13px] text-fx-text-muted">
                        적용 환율
                        {rateOverride && (
                          <span className="ml-1.5 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded">
                            수정됨
                          </span>
                        )}
                      </div>
                      {isLoadingRates ? (
                        <div className="text-lg text-gray-400">로딩 중...</div>
                      ) : effectiveRate > 0 ? (
                        <div className="flex items-baseline justify-end gap-1.5">
                          <span className={`text-xl font-bold ${rateOverride ? 'text-orange-600' : 'text-fx-text-dark'}`}>
                            {effectiveRate.toLocaleString()}
                          </span>
                          {rateOverride && (
                            <span className="text-xs text-gray-400 line-through">
                              ({currentRate.toLocaleString()})
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="text-lg text-red-500">환율 없음</div>
                      )}
                      {!rateOverride && effectiveRate > 0 && (
                        <div className="text-sm text-gray-400">두 번 탭하여 환율 변경</div>
                      )}
                    </div>
                  </div>

                  {tradeType === 'buy' ? (
                    // 매입 모드: 외화 입력 → 원화 계산
                    <>
                      {/* 외화 금액 → 원화 금액 (목업 레이아웃 — 가로 배치) */}
                      <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
                        <div>
                          <label className="block text-sm font-medium text-fx-text-muted mb-1.5">
                            외화 금액
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formatForeignAmount(foreignAmount)}
                            onChange={(e) => setForeignAmount(extractNumbers(e.target.value))}
                            className="w-full h-14 px-3 border-2 border-fx-buy bg-blue-50/40 rounded-lg focus:ring-2 focus:ring-fx-buy focus:border-transparent text-right text-2xl font-bold disabled:opacity-50"
                            placeholder="0"
                            disabled={isLoadingRates || !effectiveRate}
                          />
                        </div>
                        <span className="text-xl text-gray-400 pb-3">→</span>
                        <div>
                          <label className="block text-sm font-medium text-fx-text-muted mb-1.5 text-right">
                            환산 원화
                          </label>
                          <div className="h-14 flex items-center justify-end text-2xl leading-none font-bold text-fx-text-dark">
                            {calculatedKRW.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    // 매각 모드
                    <>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSellInputMode('krw')
                            setForeignSellInput('')
                          }}
                          className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all ${
                            sellInputMode === 'krw'
                              ? 'bg-fx-sell text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          원화 입력
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSellInputMode('foreign')
                            setInputKRW('')
                          }}
                          className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all ${
                            sellInputMode === 'foreign'
                              ? 'bg-fx-sell text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          외화 입력
                        </button>
                      </div>

                      {sellInputMode === 'krw' ? (
                        <>
                          {/* 받은 원화 → 나갈 외화 (가로 배치) */}
                          <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
                            <div>
                              <label className="block text-sm font-medium text-fx-text-muted mb-1.5">
                                받은 원화
                              </label>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={formatForeignAmount(inputKRW)}
                                onChange={(e) => {
                                  setInputKRW(extractNumbers(e.target.value))
                                  setIsCounterKRW(false)
                                }}
                                className="w-full h-14 px-3 border-2 border-fx-sell bg-orange-50/40 rounded-lg focus:ring-2 focus:ring-fx-sell focus:border-transparent text-right text-2xl font-bold disabled:opacity-50"
                                placeholder="0"
                                disabled={isLoadingRates || !effectiveRate}
                              />
                            </div>
                            <span className="text-xl text-gray-400 pb-3">→</span>
                            <div
                              onClick={handleForeignAmountTap}
                              className="cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
                            >
                              <div className="flex items-center justify-end gap-2 mb-1.5">
                                <label className="text-sm font-medium text-fx-text-muted">
                                  나갈 외화
                                  {sellCalculation.isOverridden && (
                                    <span className="ml-1.5 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded">
                                      수정됨
                                    </span>
                                  )}
                                </label>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setUseTenUnit(!useTenUnit)
                                  }}
                                  className={`text-xs px-1.5 py-0.5 rounded transition-all ${
                                    useTenUnit
                                      ? 'bg-fx-sell text-white'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  10단위 {useTenUnit ? 'ON' : 'OFF'}
                                </button>
                              </div>
                              <div className="h-14 flex items-center justify-end gap-2">
                                <span className={`text-[2rem] leading-none font-bold ${sellCalculation.isOverridden ? 'text-orange-600' : 'text-fx-sell-dark'}`}>
                                  {sellCalculation.foreignAmount.toLocaleString()} {selectedCurrency}
                                </span>
                                {sellCalculation.isOverridden && (
                                  <span className="text-xs text-gray-400 line-through">
                                    ({sellCalculation.calculatedForeignAmount.toLocaleString()})
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* 실제 비용 / 거스름돈 (압축 행) */}
                          <div className="flex gap-3">
                            <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                              <span className="text-sm text-fx-text-muted">실제 원화 비용</span>
                              <div className="text-xl font-semibold text-fx-text-dark">
                                {sellCalculation.actualKRW.toLocaleString()} 원
                              </div>
                            </div>
                            {sellCalculation.change > 0 && (
                              <div className="flex-1 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                                <span className="text-sm font-medium text-yellow-700">거스름돈</span>
                                <div className="text-xl font-bold text-yellow-700">
                                  {sellCalculation.change.toLocaleString()} 원
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          {/* 줄 외화 → 받아야 할 원화 (가로 배치) */}
                          <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
                            <div>
                              <label className="block text-sm font-medium text-fx-text-muted mb-1.5">
                                줄 외화 ({selectedCurrency})
                              </label>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={formatForeignAmount(foreignSellInput)}
                                onChange={(e) => {
                                  setForeignSellInput(extractNumbers(e.target.value))
                                }}
                                className="w-full h-14 px-3 border-2 border-fx-sell bg-orange-50/40 rounded-lg focus:ring-2 focus:ring-fx-sell focus:border-transparent text-right text-2xl font-bold disabled:opacity-50"
                                placeholder="0"
                                disabled={isLoadingRates || !effectiveRate}
                              />
                            </div>
                            <span className="text-xl text-gray-400 pb-3">→</span>
                            <div>
                              <label className="block text-sm font-medium text-fx-text-muted mb-1.5 text-right">
                                받아야 할 원화
                              </label>
                              <div className="h-14 flex items-center justify-end text-2xl leading-none font-bold text-fx-sell-dark">
                                {sellForeignCalculation.requiredKRW.toLocaleString()}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* 현재 입력을 별도 거래 항목으로 추가 (#5, #9) */}
                  {!showReprintButton && (
                    <button
                      type="button"
                      onClick={commitCurrentInput}
                      disabled={!currentLine.valid}
                      className={`w-full py-3 rounded-lg border-2 border-dashed font-bold text-sm transition-all ${
                        currentLine.valid
                          ? 'border-fx-buy text-fx-buy hover:bg-blue-50'
                          : 'border-gray-200 text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      ＋ 거래 항목 추가하기
                    </button>
                  )}

                  {/* 고객 정보 / 메모 입력 링크 - 추후 구현 */}
                  {/* <button className="text-sm text-blue-600 hover:text-blue-700 text-left mt-2">
                    고객 정보 / 메모 입력
                  </button> */}
                </div>

                {/* 이번 거래 항목 — 같은 통화도 환율이 다르면 별도 표시 (#5, #9) */}
                {allLines.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 flex flex-col flex-1 min-h-0">
                    <div className="flex items-center justify-between px-4 pt-3 pb-2">
                      <span className="text-base font-bold text-fx-text-dark">이번 거래 항목</span>
                      <span className="text-sm text-fx-text-muted">{allLines.length}건</span>
                    </div>
                    <div className="flex-1 overflow-y-auto px-3 pb-2 flex flex-col gap-1.5">
                      {/* 한 줄 포맷: [국기] [통화명] [외화금액] × [환율] ... = [원화금액] */}
                      {cart.map((line, index) => (
                        <div
                          key={getCartLineKey(line)}
                          className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-gray-200 bg-white"
                        >
                          <FlagIcon
                            countryCode={currencies.find(c => c.code === line.currency)?.countryCode || ''}
                            size="sm"
                          />
                          <span className="text-base font-bold text-fx-text-dark">{line.currency}</span>
                          <span className="text-base font-bold text-fx-text-dark">{line.foreignAmount.toLocaleString()}</span>
                          <span className={`text-base ${line.rateOverridden ? 'text-orange-600 font-bold' : 'text-gray-500'}`}>
                            × {line.rate.toLocaleString()}{line.rateOverridden && <span className="text-xs align-middle"> (수정됨)</span>}
                          </span>
                          <span className="flex-1" />
                          <span className="text-base font-bold text-fx-text-dark whitespace-nowrap">
                            = {line.krwAmount.toLocaleString()} 원
                          </span>
                          {!showReprintButton && (
                            <button
                              type="button"
                              onClick={() => handleRemoveCartLine(index)}
                              className="w-7 h-7 rounded-md border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-300 text-sm flex-none"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      {currentLine.valid && (
                        <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border ${tradeType === 'buy' ? 'border-fx-buy bg-blue-50' : 'border-fx-sell bg-orange-50'}`}>
                          <FlagIcon
                            countryCode={selectedCurrencyInfo.countryCode}
                            size="sm"
                          />
                          <span className="text-base font-bold text-fx-text-dark">{selectedCurrency}</span>
                          <span className={`text-xs font-bold ${tradeType === 'buy' ? 'text-fx-buy' : 'text-fx-sell'}`}>입력 중</span>
                          <span className="text-base font-bold text-fx-text-dark">{currentLine.foreignAmount.toLocaleString()}</span>
                          <span className={`text-base ${rateOverride ? 'text-orange-600 font-bold' : 'text-gray-500'}`}>
                            × {effectiveRate.toLocaleString()}{rateOverride && <span className="text-xs align-middle"> (수정됨)</span>}
                          </span>
                          <span className="flex-1" />
                          <span className="text-base font-bold text-fx-text-dark whitespace-nowrap">
                            = {currentLine.krwAmount.toLocaleString()} 원
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 총액 바 — 항상 표시 (#8) */}
                <div className="flex-none bg-white rounded-lg border-2 border-gray-300 overflow-hidden">
                  {/* 계수기 결과 행 (총액 바에 통합, 상태색은 행 전체 배경 — #8-3) */}
                  {countedKRW > 0 && (() => {
                    const diff = countedKRW - totalKRW
                    const state = diff === 0 ? 'match' : diff < 0 ? 'short' : 'over'
                    const rowClass = {
                      match: 'bg-[#dff4e9] text-[#0f6b46] border-[#a9dcc3]',
                      short: 'bg-[#fdf1cf] text-[#8a6100] border-[#ecd08a]',
                      over: 'bg-[#fbdedb] text-[#b3261e] border-[#f0b3ad]',
                    }[state]
                    const stateText = {
                      match: '일치',
                      short: `부족 ₩${Math.abs(diff).toLocaleString()}`,
                      over: `초과 ₩${Math.abs(diff).toLocaleString()}`,
                    }[state]
                    return (
                      <div className={`flex items-center gap-3 px-4 py-2.5 border-b ${rowClass}`}>
                        <span className="text-sm font-bold">계수된 금액</span>
                        <span className="text-lg font-bold">₩ {countedKRW.toLocaleString()}</span>
                        <span className="text-sm font-bold">{stateText}</span>
                        <span className="flex-1" />
                        <button
                          type="button"
                          onClick={() => setCountedKRW(0)}
                          className="text-xs underline opacity-70 hover:opacity-100"
                        >
                          계수 초기화
                        </button>
                      </div>
                    )
                  })()}
                  <div className="px-4 py-3">
                    <div className="text-base font-bold text-fx-text-muted">
                      {tradeType === 'buy' ? '고객 지급 원화 총액' : '고객 수취 원화 총액'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {allLines.length === 0
                        ? '금액을 입력하세요'
                        : allLines.length === 1
                          ? `${allLines[0].currency} ${allLines[0].foreignAmount.toLocaleString()} × ${allLines[0].rate.toLocaleString()}`
                          : `${allLines.length}건 합계`}
                    </div>
                    <div className="text-5xl font-bold tracking-tight text-right text-[#0f3fa8]">
                      ₩ {totalKRW.toLocaleString()}
                    </div>
                  </div>
                </div>

              </div>

              {/* 키패드 섹션 */}
              <div className="w-64 lg:w-72 fhd:w-80 flex flex-col gap-3">
                <Keypad
                  value={
                    tradeType === 'buy'
                      ? foreignAmount
                      : sellInputMode === 'krw'
                        ? inputKRW
                        : foreignSellInput
                  }
                  onChange={
                    tradeType === 'buy'
                      ? setForeignAmount
                      : sellInputMode === 'krw'
                        ? (value) => {
                          setInputKRW(value)
                          setIsCounterKRW(false)
                        }
                        : setForeignSellInput
                  }
                  disabled={isLoadingRates || !effectiveRate || showReprintButton}
                />

                {/* 여권/신분증 버튼 또는 영수증 재출력 버튼 */}
                {!showReprintButton ? (
                  <button
                    type="button"
                    onClick={() => setShowPassportScanModal(true)}
                    className={`w-full h-14 lg:h-16 fhd:h-20 text-lg lg:text-xl font-bold bg-white border-2 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      scannedPassportImage
                        ? 'text-green-600 border-green-500'
                          : passportSkipped
                            ? 'text-gray-500 border-gray-400'
                          : (tradeType === 'buy'
                            ? (foreignAmount && parseFloat(foreignAmount) > 0)
                            : sellInputMode === 'krw'
                              ? (inputKRW && sellFinalValues.foreignAmount > 0)
                              : (foreignSellInput && sellFinalValues.foreignAmount > 0))
                            ? 'text-fx-text-dark border-gray-300 animate-border-pulse'
                            : 'text-fx-text-dark border-gray-300'
                    }`}
                  >
                    {scannedPassportImage ? '여권/신분증 ✓' : passportSkipped ? '여권/신분증 (SKIP)' : '여권/신분증'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleReprint}
                    disabled={isCreatingTransaction}
                    className="w-full h-14 lg:h-16 fhd:h-20 text-lg lg:text-xl font-bold bg-blue-500 text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCreatingTransaction ? '재출력 중...' : '영수증 재출력'}
                  </button>
                )}

                {/* 매입등록 / 새 거래 버튼 */}
                {!showReprintButton ? (
                  <button
                    type="button"
                    onClick={() => setShowConfirmModal(true)}
                    className="w-full h-14 lg:h-16 fhd:h-20 text-lg lg:text-xl font-bold bg-fx-confirm text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={
                      allLines.length === 0 || isLoadingRates || (!scannedPassportImage && !passportSkipped)
                    }
                  >
                    <span className="flex flex-col items-center leading-tight">
                      <span>{tradeType === 'buy' ? '매입등록' : '매각등록'}</span>
                      {hasMultipleLines && (
                        <span className="text-xs font-medium opacity-90">
                          {allLines.length}건 · ₩ {totalKRW.toLocaleString()}
                        </span>
                      )}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleNewTransaction}
                    className="w-full h-14 lg:h-16 fhd:h-20 text-lg lg:text-xl font-bold bg-fx-confirm text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all"
                  >
                    새 거래
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ConfirmModal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        title={tradeType === 'buy' ? '매입 등록' : '매각 등록'}
        confirmText="확인"
        cancelText="취소"
        onConfirm={() => {
          setShowConfirmModal(false)
          handleComplete()
        }}
        onCancel={() => setShowConfirmModal(false)}
      >
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          {cart.length > 0 ? (
            // 복수 거래 항목 확인 (카트 사용 시 — 1건만 담겨 있어도 카트 기준으로 표시)
            <>
              {allLines.map(line => (
                <div key={getCartLineKey(line)} className="flex justify-between items-center pb-2 border-b border-gray-200 last:border-0">
                  <div>
                    <span className="text-lg font-bold text-fx-text-dark">{line.currency}</span>
                    <span className={`ml-2 text-sm ${line.rateOverridden ? 'text-orange-600' : 'text-fx-text-muted'}`}>
                      환율 {line.rate.toLocaleString()}{line.rateOverridden && ' (수정됨)'}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-fx-text-dark">
                      {line.foreignAmount.toLocaleString()} {line.currency}
                    </div>
                    <div className="text-sm text-fx-text-muted">{line.krwAmount.toLocaleString()} 원</div>
                  </div>
                </div>
              ))}
              <div className="border-t-2 border-gray-300 pt-3 flex justify-between items-center">
                <span className="text-fx-text-muted font-medium">
                  {tradeType === 'buy' ? '고객 지급 원화 총액' : '고객 수취 원화 총액'}
                </span>
                <span className={`text-2xl font-bold ${tradeType === 'buy' ? 'text-fx-buy-dark' : 'text-fx-sell-dark'}`}>
                  {totalKRW.toLocaleString()} 원
                </span>
              </div>
            </>
          ) : tradeType === 'buy' ? (
            // 매입 확인
            <>
              <div className="flex justify-between items-center">
                <span className="text-fx-text-muted">외화</span>
                <span className="text-xl font-bold text-fx-text-dark">
                  {parseFloat(foreignAmount || '0').toLocaleString()} {selectedCurrency}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-fx-text-muted">적용 환율</span>
                <span className={`text-lg ${rateOverride ? 'text-orange-600' : 'text-fx-text-dark'}`}>
                  {effectiveRate.toLocaleString()}
                  {rateOverride && <span className="ml-1 text-xs">(수정됨)</span>}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                <span className="text-fx-text-muted">원화</span>
                <span className="text-2xl font-bold text-fx-buy-dark">
                  {calculatedKRW.toLocaleString()} 원
                </span>
              </div>
            </>
          ) : (
            // 매각 확인
            <>
              <div className="flex justify-between items-center">
                {sellInputMode === 'krw' ? (
                  <>
                    <span className="text-fx-text-muted">받은 원화</span>
                    <span className="text-xl font-bold text-fx-text-dark">
                      {parseFloat(inputKRW || '0').toLocaleString()} 원
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-fx-text-muted">줄 외화</span>
                    <span className="text-xl font-bold text-fx-text-dark">
                      {sellFinalValues.foreignAmount.toLocaleString()} {selectedCurrency}
                    </span>
                  </>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-fx-text-muted">적용 환율</span>
                <span className={`text-lg ${rateOverride ? 'text-orange-600' : 'text-fx-text-dark'}`}>
                  {effectiveRate.toLocaleString()}
                  {rateOverride && <span className="ml-1 text-xs">(수정됨)</span>}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-fx-text-muted">나갈 외화</span>
                  <span className="text-2xl font-bold text-fx-sell-dark">
                    {sellFinalValues.foreignAmount.toLocaleString()} {selectedCurrency}
                  </span>
                </div>
                {sellInputMode === 'foreign' && (
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-fx-text-muted">받아야 할 원화</span>
                    <span className="text-lg text-fx-text-dark">
                      {sellFinalValues.actualKRW.toLocaleString()} 원
                    </span>
                  </div>
                )}
                {sellInputMode === 'krw' && (
                  <>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-fx-text-muted">실제 비용</span>
                      <span className="text-lg text-fx-text-dark">
                        {sellFinalValues.actualKRW.toLocaleString()} 원
                      </span>
                    </div>
                    {sellFinalValues.change > 0 && (
                      <div className="flex justify-between items-center mt-2 pt-2 border-t border-yellow-200 bg-yellow-50 -mx-4 px-4 py-2 rounded-b-lg">
                        <span className="text-yellow-700 font-medium">거스름돈</span>
                        <span className="text-xl font-bold text-yellow-700">
                          {sellFinalValues.change.toLocaleString()} 원
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </ConfirmModal>

      {/* LoadingModal */}
      <LoadingModal
        isOpen={isCreatingTransaction}
        message={loadingMessage}
      />

      {/* PassportScanModal */}
      <PassportScanModal
        isOpen={showPassportScanModal}
        onClose={() => setShowPassportScanModal(false)}
        onScanComplete={(imageDataUrl) => {
          setScannedPassportImage(imageDataUrl)
          setPassportSkipped(false)
        }}
        onSkip={() => {
          setScannedPassportImage(null)
          setPassportSkipped(true)
        }}
      />

      {/* RateModificationModal (환율 수정) */}
      <RateModificationModal
        isOpen={showRateModal}
        currentRate={currentRate}
        currencyCode={selectedCurrency}
        tradeType={tradeType}
        onConfirm={handleRateConfirm}
        onCancel={() => setShowRateModal(false)}
      />

      <ForeignAmountModificationModal
        isOpen={showForeignAmountModal}
        calculatedAmount={sellCalculation.calculatedForeignAmount}
        currencyCode={selectedCurrency}
        minUnit={CURRENCY_MIN_UNITS[selectedCurrency] || 1}
        onConfirm={handleForeignAmountConfirm}
        onCancel={() => setShowForeignAmountModal(false)}
      />

      {/* TransactionResultModal (영수증 발행 실패 시) */}
      <TransactionResultModal
        isOpen={showResultModal}
        onClose={() => {
          setShowResultModal(false)
          setResultModalError(null)
          setShowReprintButton(true)
        }}
        onReprint={async () => {
          setShowResultModal(false)
          setResultModalError(null)
          await handleReprint()
          setShowReprintButton(true)
        }}
        transactionData={lastTransaction.transactionData}
        errorMessage={resultModalError || undefined}
      />

      {/* 재출력 에러 모달 */}
      <ConfirmModal
        isOpen={showReprintErrorModal}
        title="재출력 실패"
        confirmText="확인"
        cancelText=""
        onConfirm={() => {
          setShowReprintErrorModal(false)
          setReprintErrorMessage(null)
        }}
        onCancel={() => {
          setShowReprintErrorModal(false)
          setReprintErrorMessage(null)
        }}
      >
        <div className="text-center space-y-2">
          <p className="text-red-600 font-medium">{reprintErrorMessage}</p>
          <p className="text-fx-text-muted text-sm">프린터 연결 상태를 확인해주세요.</p>
        </div>
      </ConfirmModal>

      {/* 시재 페이지 (나중에 구현) */}
      {mainTab === 'cash' && (
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">시재 관리 페이지 (추후 구현)</p>
        </div>
      )}

      {/* 기록 페이지 */}
      {mainTab === 'history' && <HistoryTab />}

      {/* 마감 페이지 */}
      {mainTab === 'close' && <CloseTab />}

      {/* 거래 페이지 (지점간 송금/수취) */}
      {mainTab === 'transfer' && <TransferTab />}
    </div>
  )
}

export default Home
